import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { CustomizedStaticDateCalendar } from "../../../../../../staticCalendar"
import { CustomizedStaticDigitalTime } from "../../../../../../staticDigitalTime"
import { PickerProps } from "./types"
import { Controller, useFormContext } from "react-hook-form"
import dayjs, { Dayjs } from "dayjs"
import { useState } from "react"

const maxValue = ({ max, startLower, value }: { max?: Dayjs; startLower?: boolean; value: Dayjs }) => {
    if (startLower && dayjs(max).isAfter(dayjs(value), 'day')) {
        return dayjs(max).endOf('day')
    }
    return max
}
const minValue = ({ min, value }: { min?: Dayjs; max?: Dayjs; value: Dayjs; isStart?: boolean; }) => {
    const localeValue = dayjs(value)
    const localeMin = dayjs(min)
    if (localeValue?.isAfter(localeMin, 'day')) {
        return localeMin.startOf('day')
    }
    return localeMin
}
const Pickers = ({ min, max, calendarProps, timeClockProps, name, is }: PickerProps) => {
    const { control, setValue, getValues } = useFormContext()
    const value = getValues(name)
    const [localeMin, setLocaleMin] = useState(is?.endBigger && is?.end ? min?.startOf('day') : minValue({ min, max, value, isStart: is?.start }))
    const [localeMax, setLocaleMax] = useState(is?.start || is?.end ? maxValue({ max, startLower: is.start || is?.end, value, }) : max)
    const validateDateAndFormat = ({ newDate, nameToGetDate }: { newDate: Dayjs; nameToGetDate: 'time' | 'date' }) => {
        if (is?.end) {
            const localeMin = dayjs(min)
            const dateTime = getValues(`${name}-${nameToGetDate}`)
            let fechaCombinada = dayjs(newDate)
            if (nameToGetDate == 'time') {
                fechaCombinada = fechaCombinada.hour(dateTime.hour()).minute(dateTime.minute()).second(dateTime.second())
            }
            if (nameToGetDate == 'date') {
                fechaCombinada = fechaCombinada.year(dateTime.year()).month(dateTime.month()).date(dateTime.date())
            }
            const isEndLower = dayjs(fechaCombinada).isBefore(dayjs(min))
            const isStartSameDayThanMax = fechaCombinada.isSame(localeMax, 'day')
            if (isStartSameDayThanMax) {
                setLocaleMax(max)
            } else if (!isStartSameDayThanMax && !!max) {
                setLocaleMax(dayjs(max).endOf('day'))
            }
            if (isEndLower) {
                let fechaCombinadaMax = dayjs(newDate)

                fechaCombinadaMax = fechaCombinadaMax.hour(dayjs(min).hour()).minute(dayjs(min).minute()).second(dayjs(min).second()).add(5, 'minute')
                setValue(`${name}-time`, fechaCombinadaMax)
            }
            if (fechaCombinada.isAfter(localeMin, 'day')) {
                setLocaleMin(dayjs(min).startOf('day'))
            } else {
                setLocaleMin(min)
            }
        }
        if (is?.start) {
            const localeMax = dayjs(max)
            const localeMin = dayjs(min)
            const dateTime = getValues(`${name}-${nameToGetDate}`)
            let fechaCombinada = dayjs(newDate)
            if (nameToGetDate == 'time') {
                fechaCombinada = fechaCombinada.hour(dateTime.hour()).minute(dateTime.minute()).second(dateTime.second())
            }
            if (nameToGetDate == 'date') {
                fechaCombinada = fechaCombinada.year(dateTime.year()).month(dateTime.month()).date(dateTime.date())
            }
            const isStartBigger = fechaCombinada.isAfter(localeMax)
            const isStartDayLower = fechaCombinada.isBefore(localeMax, 'day')
            const isStartSameDayThanMax = fechaCombinada.isSame(localeMax, 'day')
            let fechaCombinadaMax = dayjs(fechaCombinada)

            if (fechaCombinadaMax.isBefore((min))) {
                setValue(`${name}-time`, min)
                setLocaleMin(min)
            } else {
                if (!!isStartBigger) {
                    fechaCombinadaMax = fechaCombinadaMax.hour(localeMax.hour()).minute(localeMax.minute()).second(localeMax.second()).subtract(5, 'minute')
                    setValue(`${name}-time`, fechaCombinadaMax)
                }
                if (isStartDayLower || !max) {
                    setLocaleMax(localeMax.endOf('day'))
                    if (fechaCombinada.isAfter(dayjs(min), 'day')) {
                        setLocaleMin(dayjs(min).startOf('day'))
                    } else {
                        if (fechaCombinada.isBefore(dayjs(min))) {
                            setValue(`${name}-time`, fechaCombinadaMax.hour(localeMin.hour()).minute(localeMin.minute()).second(localeMin.second()).add(5, 'minute'))
                        }
                        setLocaleMin(min)
                    }
                } else if (isStartSameDayThanMax) {
                    setLocaleMax(max)
                    if (fechaCombinada.isAfter(dayjs(min), 'day')) {
                        setLocaleMin(dayjs(min).startOf('day'))
                    } else {
                        setLocaleMin(min)
                    }
                } else {
                    setLocaleMax(max)
                    setLocaleMin(min)
                }
            }
        }
    }
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Controller
                render={({ field }) => {
                    return (
                        <CustomizedStaticDateCalendar
                            {...field}
                            {...calendarProps}
                            views={['month', 'year', 'day']}
                            maxDate={localeMax}
                            minDate={localeMin}
                            onChange={(newValue) => {
                                setValue(`${name}-date`, newValue)
                                validateDateAndFormat({ newDate: newValue, nameToGetDate: 'time' })
                            }}
                        />
                    )
                }}
                control={control}
                name={`${name}-date`}
                defaultValue={10}
            />

            <Controller
                render={({ field: { ...rest } }) => {
                    return (
                        <CustomizedStaticDigitalTime
                            {...rest}
                            {...timeClockProps}
                            maxTime={localeMax}
                            minTime={localeMin}
                            ampm
                            timeSteps={{ minutes: 1 }}
                            onChange={(newValue) => {
                                setValue(`${name}-time`, newValue)
                                validateDateAndFormat({ newDate: newValue, nameToGetDate: 'date' })
                            }}
                            sx={{
                                width: 'fit-content',
                                borderBottom: 'none',
                                '& ::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                '-ms-overflow-style': 'none', // IE and Edge
                                'scrollbar-width': 'none', // Firefox
                            }}
                        />
                    )
                }}
                control={control}
                name={`${name}-time`}
                defaultValue={10}
            />

        </LocalizationProvider>
    )
}

export { Pickers }
